import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, X, Star, Paperclip, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import PriorityPill from "../components/PriorityPill";
import SLATimer from "../components/SLATimer";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const STATUS_LABEL = {
    open: "Open",
    awaiting_agent: "Awaiting agent",
    awaiting_customer: "Awaiting your reply",
    resolved: "Resolved",
    closed: "Closed",
};

const SupportThreadContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [showRate, setShowRate] = useState(false);
    const [rating, setRating] = useState(5);
    const [feedback, setFeedback] = useState("");
    const [pollTick, setPollTick] = useState(0);
    const threadRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/support/tickets/${id}`);
            setTicket(data);
            setMessages(data.messages || []);
        } catch (err) {
            if (err?.response?.status === 404) {
                toast.error("Ticket not found");
                navigate("/support");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [id]);

    // Poll every 15s to pull agent replies
    useEffect(() => {
        const t = setInterval(() => setPollTick((x) => x + 1), 15_000);
        return () => clearInterval(t);
    }, []);
    useEffect(() => { if (pollTick > 0) load(); /* eslint-disable-next-line */ }, [pollTick]);

    useEffect(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, [messages]);

    const send = async () => {
        const content = reply.trim();
        if (!content || sending) return;
        try {
            setSending(true);
            await api.post(`/support/tickets/${id}/messages`, { content });
            setReply("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not send");
        } finally {
            setSending(false);
        }
    };

    const closeTicket = async () => {
        if (!window.confirm("Close this ticket?")) return;
        try {
            await api.post(`/support/tickets/${id}/close`);
            toast.success("Ticket closed");
            await load();
        } catch {
            toast.error("Could not close ticket");
        }
    };

    const submitRating = async () => {
        try {
            await api.post(`/support/tickets/${id}/rate`, { rating, feedback: feedback.trim() || null });
            toast.success("Thanks for your feedback!");
            setShowRate(false);
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not rate");
        }
    };

    if (loading) {
        return <div className="h-96 skeleton rounded-3xl" />;
    }
    if (!ticket) return null;

    const isClosed = ticket.status === "closed";

    return (
        <div className="rounded-3xl border border-zinc-100 bg-white shadow-soft" data-testid="support-thread">
            {/* Header */}
            <div className="border-b border-zinc-100 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs text-zinc-400">{ticket.ticket_no}</span>
                            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-700">
                                {STATUS_LABEL[ticket.status] || ticket.status}
                            </span>
                            <PriorityPill priority={ticket.priority} />
                        </div>
                        <h2 className="mt-1.5 text-lg font-semibold text-zinc-900">{ticket.subject}</h2>
                    </div>
                    <SLATimer slaDueAt={ticket.sla_due_at} slaBreached={ticket.sla_breached} status={ticket.status} />
                </div>

                {ticket.linked_summary?.order && (
                    <div className="mt-3 rounded-xl bg-zinc-50 p-3 text-xs text-zinc-700" data-testid="support-linked-order">
                        Linked order <span className="font-mono text-zinc-900">{ticket.linked_summary.order.order_no}</span> · {ticket.linked_summary.order.status} · ₹{ticket.linked_summary.order.total}
                        <Link to={`/orders/${ticket.linked_summary.order.id}`} className="ml-2 text-brand-700 hover:underline">View →</Link>
                    </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                    {ticket.status === "resolved" && !ticket.rating && (
                        <button
                            onClick={() => setShowRate(true)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-200"
                            data-testid="support-rate-btn"
                        >
                            <Star className="h-3.5 w-3.5" /> Rate experience
                        </button>
                    )}
                    {ticket.rating && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-800" data-testid="support-rated-badge">
                            <Star className="h-3.5 w-3.5 fill-current" /> You rated {ticket.rating}/5
                        </span>
                    )}
                    {!isClosed && (
                        <button
                            onClick={closeTicket}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:border-rose-200 hover:text-rose-600"
                            data-testid="support-close-btn"
                        >
                            <X className="h-3.5 w-3.5" /> Close ticket
                        </button>
                    )}
                </div>
            </div>

            {/* Thread */}
            <div ref={threadRef} className="h-[500px] space-y-3 overflow-y-auto bg-zinc-50/40 p-5" data-testid="support-thread-messages">
                {messages.map((m) => {
                    const isCustomer = m.sender_type === "customer";
                    const isSystem = m.sender_type === "system";
                    if (isSystem) {
                        return (
                            <div key={m.id} className="my-2 text-center" data-testid={`support-msg-${m.id}`}>
                                <span className="rounded-full bg-zinc-200 px-3 py-0.5 text-[10px] uppercase tracking-wider text-zinc-600">
                                    {m.content}
                                </span>
                            </div>
                        );
                    }
                    return (
                        <div key={m.id} className={`flex ${isCustomer ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.id}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                                isCustomer ? "bg-brand-700 text-white" : "bg-white text-zinc-900 ring-1 ring-zinc-100"
                            }`}>
                                <p className="whitespace-pre-wrap break-words">{m.content}</p>
                                <p className={`mt-1 text-[10px] ${isCustomer ? "text-white/70" : "text-zinc-400"}`}>
                                    {m.sender_name || (isCustomer ? "You" : "PILLS")} · {new Date(m.created_at).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Reply bar */}
            {!isClosed && (
                <div className="border-t border-zinc-100 p-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
                            placeholder="Type a reply… (Ctrl+Enter to send)"
                            rows={2}
                            maxLength={4000}
                            className="flex-1 resize-none rounded-2xl border border-zinc-200 p-3 text-sm outline-none focus:border-brand-500"
                            data-testid="support-reply-input"
                        />
                        <button
                            onClick={send}
                            disabled={!reply.trim() || sending}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-700 text-white shadow-sm hover:bg-brand-800 disabled:opacity-50"
                            aria-label="Send"
                            data-testid="support-send-btn"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                    </div>
                </div>
            )}

            {/* Inline rate dialog */}
            {showRate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4" data-testid="support-rate-dialog">
                    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
                        <p className="text-xs font-semibold uppercase tracking-wider text-brand-600">Feedback</p>
                        <h3 className="mt-1 text-xl font-semibold">How was your experience?</h3>
                        <div className="my-4 flex items-center justify-center gap-1.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} onClick={() => setRating(n)} className="p-1" data-testid={`rate-star-${n}`}>
                                    <Star className={`h-7 w-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
                                </button>
                            ))}
                        </div>
                        <textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="Optional feedback…"
                            rows={3}
                            className="w-full rounded-xl border border-zinc-200 p-3 text-sm outline-none focus:border-brand-500"
                            data-testid="rate-feedback-input"
                        />
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => setShowRate(false)} className="rounded-full border border-zinc-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={submitRating} className="rounded-full bg-brand-700 px-5 py-2 text-sm text-white" data-testid="rate-submit-btn">Submit</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SupportThread = () => (
    <Layout>
        <section className="mx-auto max-w-4xl px-6 py-10 lg:px-12">
            <Link to="/support" className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700" data-testid="support-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to tickets
            </Link>
            <AuthGuard>
                <SupportThreadContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default SupportThread;
